import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { PageContainer } from '../../../common/components/PageContainer';
import { MarketData, useMarketApi } from '../../../common/hooks/use-market-api.hook';
import { ROUTES } from '../../../modules/vendor/market/insight-spec';
import { BacklinksView } from './backlinks-view';
import { CompetitorsView } from './competitors-view';
import { GapsView } from './gaps-view';
import { HealthView } from './health-view';
import { NotAnalysed, Provenance } from './market-shared';
import { ProfileView } from './market-views';
import { OverviewView } from './overview-view';
import { SearchView } from './search-view';

export type MarketView = 'overview' | 'search' | 'competitors' | 'backlinks' | 'profile' | 'gaps' | 'health';

/**
 * What search knows about the vendor.
 *
 * Seven views of one read: the API returns everything at once, so switching
 * between them costs nothing and the numbers can never disagree with each
 * other. Nothing here fetches from DataForSEO — it shows what an analysis
 * already bought, and says so when there is none.
 *
 * Every view opens with a written reading (what changed, what works, what to
 * fix), then the headline numbers, then the pictures, then the detail.
 */
export const MarketPage: React.FC<{ view: MarketView }> = ({ view }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const market = useMarketApi();

    const [data, setData] = useState<MarketData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        market
            .get()
            .then((d) => alive && setData(d))
            .catch(() => alive && setData(null))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const title = t(`MARKET.${view.toUpperCase()}.TITLE`);

    return (
        <PageContainer breadcrumbs={[{ label: t('MARKET.TITLE') }, { label: title }]}>
            <div className="mx-auto flex w-full min-w-0 max-w-[1100px] flex-col gap-6" data-testid={`business-${view}`}>
                <div>
                    <h1 className="font-heading text-[22px] font-medium tracking-tight">{title}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{t(`MARKET.${view.toUpperCase()}.SUBTITLE`)}</p>
                </div>

                {loading && (
                    <div className="flex justify-center py-16">
                        <Loader2 className="size-5 animate-spin text-muted-foreground" />
                    </div>
                )}

                {!loading && !data?.analysed && <NotAnalysed onGo={() => navigate(ROUTES.analyse)} />}

                {!loading && data?.analysed && (
                    <>
                        <Provenance data={data} />
                        {view === 'overview' && <OverviewView data={data} />}
                        {view === 'search' && <SearchView data={data} />}
                        {view === 'competitors' && <CompetitorsView data={data} />}
                        {view === 'backlinks' && <BacklinksView data={data} />}
                        {view === 'profile' && <ProfileView data={data} />}
                        {view === 'gaps' && <GapsView data={data} />}
                        {view === 'health' && <HealthView data={data} />}
                    </>
                )}
            </div>
        </PageContainer>
    );
};

export default MarketPage;
